import React, { useEffect, useRef, useState } from 'react';
import { projectService, employeeService } from '../../services/api';
import Drawer from '../Common/Drawer';
import EmployeeCombobox from './EmployeeCombobox';
import './ProjectPropertiesDrawer.css';

// AS-S2 (P4): PP-S1 modal migrated to the shared <Drawer>. Same logic as
// the previous ProjectPropertiesModal — only the chrome changed.
// RA-S1 (2026-07-16): + Project Manager picker (EmployeeCombobox). The
// field is informational — caller.isPm in the delivery layer stays the
// global ManageProjects permission — but is the seed of the "PM of a
// project" column in the reports and (later) the RA-cleanup slices.

const CURRENCY_RE = /^[A-Za-z]{3}$/;
const CODE_RE = /^[A-Za-z0-9_-]+$/;

const initialState = (mode, project) => mode === 'create'
  ? { code: '', name: '', clientName: '', description: '', currencyCode: 'EUR', isBillable: true, isOpenToAllConsultants: false, projectManagerEmployeeId: 0 }
  : {
      code: project?.code || '',
      name: project?.name || '',
      clientName: project?.clientName || '',
      description: project?.description || '',
      currencyCode: project?.currencyCode || 'EUR',
      isBillable: project?.isBillable !== false,
      isOpenToAllConsultants: project?.isOpenToAllConsultants === true,
      // RA-S1 — 0 = "no PM" (matches the server-side sentinel on
      // UpdateProject: 0 clears, positive sets). Never send `null`
      // because that means "do not touch" server-side; we always echo
      // back what the drawer sees so the drawer is authoritative.
      projectManagerEmployeeId: project?.projectManagerEmployeeId || 0,
    };

const ProjectPropertiesDrawer = ({ mode, project, open, onClose, onSuccess }) => {
  const [fields, setFields] = useState(() => initialState(mode, project));
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [employees, setEmployees] = useState([]);
  const firstFieldRef = useRef(null);

  useEffect(() => {
    if (open) {
      setFields(initialState(mode, project));
      setErrors({});
      setServerError(null);
      setTimeout(() => firstFieldRef.current?.focus(), 0);
    }
  }, [open, mode, project]);

  // RA-S1 — load the employee lookup once the drawer opens; the PM
  // picker needs it. Lookup returns a slim list (id + fullName +
  // email + position + department) — same shape EmployeeCombobox
  // consumes on the assignment form.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    employeeService.lookup()
      .then((list) => { if (!cancelled) setEmployees(Array.isArray(list) ? list : []); })
      .catch(() => { /* non-fatal — the picker just stays empty */ });
    return () => { cancelled = true; };
  }, [open]);

  if (!open) return null;

  const isCreate = mode === 'create';

  const validate = () => {
    const e = {};
    if (isCreate) {
      const code = fields.code.trim();
      if (!code) e.code = 'Code is required.';
      else if (!CODE_RE.test(code)) e.code = 'Code: letters, digits, dash, underscore only.';
      else if (code.length > 20) e.code = 'Code: max 20 characters.';
    }
    const name = fields.name.trim();
    if (!name) e.name = 'Name is required.';
    else if (name.length > 200) e.name = 'Name: max 200 characters.';
    if (fields.clientName && fields.clientName.length > 200) e.clientName = 'Client Name: max 200 characters.';
    if (fields.description && fields.description.length > 2000) e.description = 'Description: max 2000 characters.';
    if (!CURRENCY_RE.test(fields.currencyCode.trim())) e.currencyCode = 'Currency: 3-letter code (e.g. EUR).';
    return e;
  };

  const handleSave = async () => {
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) return;

    setSaving(true);
    setServerError(null);
    try {
      let result;
      if (isCreate) {
        result = await projectService.create({
          code: fields.code.trim(),
          name: fields.name.trim(),
          clientName: fields.clientName.trim() || null,
          description: fields.description.trim() || null,
          currencyCode: fields.currencyCode.trim().toUpperCase(),
          isBillable: fields.isBillable,
          isOpenToAllConsultants: fields.isOpenToAllConsultants,
          // RA-S1 — 0 → "no PM" (server also accepts null); positive id → set.
          projectManagerEmployeeId: fields.projectManagerEmployeeId || 0,
        });
      } else {
        result = await projectService.update(project.id, {
          name: fields.name.trim(),
          clientName: fields.clientName.trim() || null,
          description: fields.description.trim() || null,
          currencyCode: fields.currencyCode.trim().toUpperCase(),
          isOpenToAllConsultants: fields.isOpenToAllConsultants,
          // RA-S1 — always send the drawer's current value so the
          // server's sentinel matches the user-visible state. 0 clears,
          // positive sets (validated against Employees on the server).
          projectManagerEmployeeId: fields.projectManagerEmployeeId || 0,
        });
      }
      onSuccess(result);
    } catch (err) {
      const data = err?.response?.data;
      const msg = (data && typeof data === 'object' && data.message)
        || (typeof data === 'string' ? data : null)
        || err?.message
        || 'Save failed.';
      setServerError(typeof msg === 'string' ? msg : 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const set = (key) => (e) => setFields((f) => ({ ...f, [key]: e.target.value }));
  const setChecked = (key) => (e) => setFields((f) => ({ ...f, [key]: e.target.checked }));

  const footer = (
    <>
      <button
        type="button"
        className="ppd-btn"
        onClick={onClose}
        disabled={saving}
        data-testid="ppd-cancel-btn"
      >
        Cancel
      </button>
      <button
        type="button"
        className="ppd-btn pri"
        onClick={handleSave}
        disabled={saving}
        data-testid="ppd-save-btn"
      >
        {saving ? 'Saving…' : (isCreate ? 'Create' : 'Save')}
      </button>
    </>
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width="md"
      title={isCreate ? 'Create Project' : 'Edit Project Properties'}
      footer={footer}
      testId="ppd-drawer"
    >
      {serverError && <div className="ppd-server-error" role="alert">{serverError}</div>}

      <div className="ppd-field">
        <label htmlFor="ppd-code">Code{isCreate && ' *'}</label>
        {isCreate ? (
          <div>
            <input
              id="ppd-code"
              ref={firstFieldRef}
              type="text"
              value={fields.code}
              onChange={set('code')}
              maxLength={20}
            />
            {errors.code && <div className="ppd-error">{errors.code}</div>}
          </div>
        ) : <span className="ppd-readonly" data-testid="ppd-code-readonly">{fields.code}</span>}
      </div>

      <div className="ppd-field">
        <label htmlFor="ppd-name">Name *</label>
        <div>
          <input
            id="ppd-name"
            ref={!isCreate ? firstFieldRef : null}
            type="text"
            value={fields.name}
            onChange={set('name')}
            maxLength={200}
          />
          {errors.name && <div className="ppd-error">{errors.name}</div>}
        </div>
      </div>

      <div className="ppd-field">
        <label htmlFor="ppd-client">Client Name</label>
        <div>
          <input
            id="ppd-client"
            type="text"
            value={fields.clientName}
            onChange={set('clientName')}
            maxLength={200}
          />
          {errors.clientName && <div className="ppd-error">{errors.clientName}</div>}
        </div>
      </div>

      <div className="ppd-field">
        <label htmlFor="ppd-description">Description</label>
        <div>
          <textarea
            id="ppd-description"
            rows={4}
            value={fields.description}
            onChange={set('description')}
            maxLength={2000}
          />
          {errors.description && <div className="ppd-error">{errors.description}</div>}
        </div>
      </div>

      <div className="ppd-field">
        <label htmlFor="ppd-currency">Currency Code *</label>
        <div>
          <input
            id="ppd-currency"
            type="text"
            value={fields.currencyCode}
            onChange={set('currencyCode')}
            maxLength={3}
            style={{ width: 80, textTransform: 'uppercase' }}
          />
          {errors.currencyCode && <div className="ppd-error">{errors.currencyCode}</div>}
        </div>
      </div>

      <div className="ppd-field">
        <label htmlFor={isCreate ? 'ppd-billable' : undefined}>Billable</label>
        {isCreate ? (
          <input
            id="ppd-billable"
            type="checkbox"
            checked={fields.isBillable}
            onChange={setChecked('isBillable')}
          />
        ) : (
          <span className="ppd-readonly" data-testid="ppd-billable-readonly">
            {fields.isBillable ? 'Billable' : 'Non-billable'}
          </span>
        )}
      </div>

      <div className="ppd-field">
        <label htmlFor="ppd-open-consultants">Open to all Consultants — auto-assigns every Consultant to every task</label>
        <input
          id="ppd-open-consultants"
          type="checkbox"
          checked={fields.isOpenToAllConsultants}
          onChange={setChecked('isOpenToAllConsultants')}
          data-testid="ppd-open-consultants"
        />
      </div>

      {/* RA-S1 — Project Manager picker. Purely informational; caller.isPm
          in delivery stays the ManageProjects permission. "— none —"
          is expressed via the combobox's ✕ clear affordance. */}
      <div className="ppd-field">
        <label>Project Manager</label>
        <EmployeeCombobox
          employees={employees}
          value={fields.projectManagerEmployeeId
            ? (employees.find((e) => e.id === fields.projectManagerEmployeeId) || null)
            : null}
          onChange={(emp) => setFields((f) => ({ ...f, projectManagerEmployeeId: emp?.id || 0 }))}
          placeholder="Search a project manager…"
          testIdPrefix="ppd-pm"
        />
      </div>

      {!isCreate && project?.createdAt && (
        <div className="ppd-field">
          <label>Created</label>
          <span className="ppd-readonly">{new Date(project.createdAt).toLocaleString()}</span>
        </div>
      )}
      {!isCreate && project?.updatedAt && (
        <div className="ppd-field">
          <label>Updated</label>
          <span className="ppd-readonly">{new Date(project.updatedAt).toLocaleString()}</span>
        </div>
      )}
    </Drawer>
  );
};

export default ProjectPropertiesDrawer;
